import React from 'react'
import styled from 'styled-components'
import { Programme } from '.'

export function AccordionItem({ data }: { data: Programme }) {
  const [isOpen, setIsOpen] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = React.useState('0px')
  const contentId = `accordion-content-${data.id}`

  const hasDescription = Boolean(data.bio && data.bio.trim().length > 0)

  const toggleAccordion = () => {
    if (!hasDescription) return
    setIsOpen((prev) => !prev)
  }

  React.useEffect(() => {
    const contentEl = contentRef.current

    if (!contentEl) return

    if (!isOpen) {
      setMaxHeight('0px')
      return
    }

    const updateHeight = () => {
      setMaxHeight(`${contentEl.scrollHeight}px`)
    }

    updateHeight()

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(updateHeight)
      observer.observe(contentEl)

      return () => observer.disconnect()
    }

    if (typeof window === 'undefined') {
      return
    }

    ;(window as Window).addEventListener('resize', updateHeight)
    return () => (window as Window).removeEventListener('resize', updateHeight)
  }, [isOpen])

  return (
    <Item $open={isOpen}>
      <HeaderButton
        type="button"
        className="pill d-flex justify-content-between align-items-center w-100"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={toggleAccordion}
      >
        <div className="d-flex align-items-center gap">
          {/* <div
            className="avatar"
            style={{
              backgroundImage: `url(${data.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div> */}
          <div>
            <span className="time">{data.time}</span>
            <p className="title">{data.title}</p>
          </div>
        </div>
        {hasDescription && (
          <div className={`collapse-btn ${isOpen ? 'collapsed' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none">
              <path
                d="M24.1429 15.3571H14.8571V24.6429C14.8571 25.1354 14.6615 25.6078 14.3132 25.9561C13.9649 26.3043 13.4925 26.5 13 26.5C12.5075 26.5 12.0351 26.3043 11.6868 25.9561C11.3385 25.6078 11.1429 25.1354 11.1429 24.6429V15.3571H1.85714C1.3646 15.3571 0.892227 15.1615 0.543945 14.8132C0.195663 14.4649 0 13.9925 0 13.5C0 13.0075 0.195663 12.5351 0.543945 12.1868C0.892227 11.8385 1.3646 11.6429 1.85714 11.6429H11.1429V2.35714C11.1429 1.8646 11.3385 1.39223 11.6868 1.04394C12.0351 0.695662 12.5075 0.5 13 0.5C13.4925 0.5 13.9649 0.695662 14.3132 1.04394C14.6615 1.39223 14.8571 1.8646 14.8571 2.35714V11.6429H24.1429C24.6354 11.6429 25.1078 11.8385 25.4561 12.1868C25.8043 12.5351 26 13.0075 26 13.5C26 13.9925 25.8043 14.4649 25.4561 14.8132C25.1078 15.1615 24.6354 15.3571 24.1429 15.3571Z"
                fill="#434343"
              />
            </svg>
          </div>
        )}
      </HeaderButton>
      <ContentWrapper id={contentId} role="region" aria-hidden={!isOpen} $open={isOpen} $maxHeight={maxHeight}>
        <div ref={contentRef} className="d-flex align-items-center gap">
          <div className="description" dangerouslySetInnerHTML={{ __html: data.bio }}></div>
        </div>
      </ContentWrapper>
    </Item>
  )
}

const Item = styled.div<{ $open: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 13px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  transition: all 0.3s ease-in-out;
  border-radius: ${({ $open }) => ($open ? '40px' : '60px')};
  background: var(--White-01, #fff);

  @media (min-width: 1140px) {
    padding: 12px 42px;
  }

  .gap {
    gap: 27px;

    @media (max-width: 768px) {
      gap: 15px;
    }
  }

  .avatar {
    background: var(--blue-01);
    border-radius: 50%;
    width: 77px;
    height: 77px;
    aspect-ratio: 1/1;
    align-self: stretch;
  }

  .time {
    color: var(--Black-01_Text, #434343);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 125% */
    margin-bottom: 10px;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }

  .title {
    color: var(--gray-900, #111827);
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 120% */

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .collapse-btn {
    margin-right: 10px;
    transition: all 0.3s ease-in-out;
    cursor: pointer;

    &.collapsed {
      transform: rotate(45deg);
    }
  }
`

const HeaderButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
`

const ContentWrapper = styled.div<{ $open: boolean; $maxHeight: string }>`
  width: 100%;
  overflow: hidden;
  max-height: ${({ $maxHeight }) => $maxHeight};
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
`
